/**
 * webSpaceBgDraw.ts
 * Canvas2D draw helper for the World 2 space-scene background layer
 * (deep-space colour wash, tiled starfield, mid-distance decorations).
 * Extracted from `webSpaceDraw.ts` so the parent file stays under
 * the AGENTS.md 300-line cap once the missile sprite + camera-shake
 * stanza landed.
 *
 * Module-local: owns the `bgFarSlot` cache and the starfield asset
 * `require`. The parent calls {@link drawSpaceBackground} once per
 * frame before the camera-shake save/translate so the wash and
 * starfield stay fixed even when the playfield is shaking.
 *
 * @module webSpaceBgDraw
 */

/// <reference lib="dom" />

import type { SpaceDrawList } from './spaceRender';
import { drawBgDecoration } from './webSpaceBgDecorationsDraw';
import { SPACE_BG_FAR_MODULES } from './spaceSpriteManifest';
import { VIEWPORT_WIDTH, VIEWPORT_HEIGHT } from '../../constants/gameConstants';
import {
  type ImageSlot,
  createImageSlot,
  ensureImageLoaded,
} from './lazyImageLoader';

// Module-level cache slot for the tiled starfield. `lazyImageLoader`
// owns the single-shot decode latch.
const bgFarSlot: ImageSlot = createImageSlot();

// DECISION: Pick one starfield variant per session, lazily on the first
// frame. Picking once (not per-frame) avoids the bg strobing between
// variants; doing it lazily (not at module load) keeps tests
// deterministic — they can call `_setBgVariantIndexForTests` before
// the first draw.
let pickedBgIndex: number | null = null;

/**
 * Test-only hook: pin the variant index used by the next paint.
 * Pass `null` to reset back to "random on next draw".
 *
 * Always invalidates the cached `bgFarSlot` so the next draw fetches
 * the sprite for the newly-pinned index. Without this clear, a test
 * that pins a non-null index after a prior draw would inherit the
 * previously-loaded variant because `loadStarted` short-circuits
 * `ensureImageLoaded()`.
 *
 * @param index - Variant index in `SPACE_BG_FAR_MODULES`, or `null`.
 * @returns void — mutates module-local state.
 */
export function _setBgVariantIndexForTests(index: number | null): void {
  pickedBgIndex = index;
  bgFarSlot.current = null;
  bgFarSlot.loadStarted = false;
}

/**
 * Test-only inspector: read the variant index that the renderer is
 * currently using. `null` means "not yet chosen — will be picked on
 * the next paint".
 *
 * @returns Variant index in `SPACE_BG_FAR_MODULES`, or `null`.
 */
export function _getBgVariantIndexForTests(): number | null {
  return pickedBgIndex;
}

/**
 * Test-only inspector: snapshot the cached far-bg image slot. Lets
 * tests verify that `_setBgVariantIndexForTests` invalidates the slot
 * so the next draw picks up the newly-pinned variant rather than
 * reusing the previously-decoded one.
 *
 * @returns `{ hasImage, loadStarted }` snapshot of `bgFarSlot`.
 */
export function _getBgSlotStateForTests(): {
  hasImage: boolean;
  loadStarted: boolean;
} {
  return {
    hasImage: bgFarSlot.current !== null,
    loadStarted: bgFarSlot.loadStarted,
  };
}

/**
 * Paint the background layer for one frame: deep-space colour wash,
 * tiled starfield (parallax-scrolled), and mid-distance decorations.
 * The shake save/translate happens AFTER this call in the parent so
 * none of these layers strobe with the playfield.
 *
 * @param ctx - 2D rendering context for the game canvas.
 * @param drawList - Per-frame draw list from {@link buildSpaceDrawList}.
 * @returns void — paints directly onto `ctx`.
 */
export function drawSpaceBackground(
  ctx: CanvasRenderingContext2D,
  drawList: SpaceDrawList,
): void {
  if (pickedBgIndex === null) {
    pickedBgIndex = Math.floor(Math.random() * SPACE_BG_FAR_MODULES.length);
  }
  ensureImageLoaded(bgFarSlot, SPACE_BG_FAR_MODULES[pickedBgIndex]!);

  // Solid background fill first — covers any decode gap so the canvas
  // never shows through and provides the deep-space colour wash that
  // sits behind the tiled starfield.
  ctx.fillStyle = drawList.background.color;
  ctx.fillRect(0, 0, VIEWPORT_WIDTH, VIEWPORT_HEIGHT);

  // Painted starfield, tiled horizontally and offset by the camera-
  // derived parallax value. Stretched to VIEWPORT_HEIGHT so the image
  // fills the viewport regardless of its source aspect ratio.
  const bg = bgFarSlot.current;
  if (bg !== null) {
    // DECISION: Disable bilinear filtering for the tile blit. The bg
    // is stretched vertically (256 px source → VIEWPORT_HEIGHT dest),
    // and with smoothing on, Canvas2D edge-clamps the leftmost and
    // rightmost source columns when sampling the scaled output. That
    // pulls in transparent / dark border pixels and paints them as a
    // visible vertical seam wherever two tile copies meet. Pixel-art
    // backdrops want nearest-neighbour sampling anyway. Saved + restored
    // so the rest of the frame keeps the default (smoothing on).
    const prevSmoothing = ctx.imageSmoothingEnabled;
    ctx.imageSmoothingEnabled = false;
    // DECISION: Compute the wrap-aligned start X with modulo so we never
    // need more than ⌈VW / bgW⌉ + 1 tiles regardless of how far the
    // camera has scrolled. Negative modulo via JS `%` is handled by
    // adding `bgW` once. Snap to integer X so tiles meet at exact
    // pixel boundaries — a fractional `startX` is the other half of
    // the seam pattern (bilerp + fractional offset = visible gap).
    const bgW = bg.naturalWidth || bg.width || VIEWPORT_WIDTH;
    const wrapped =
      ((drawList.background.parallaxOffset % bgW) + bgW) % bgW;
    let startX = Math.floor(-wrapped);
    while (startX < VIEWPORT_WIDTH) {
      ctx.drawImage(bg, startX, 0, bgW, VIEWPORT_HEIGHT);
      startX += bgW;
    }
    ctx.imageSmoothingEnabled = prevSmoothing;
  }

  // Mid-distance decorations (procedural planets, debris fields, star
  // twinkles). No decode gating — these are pure shape draws, so they
  // appear from frame zero.
  for (const d of drawList.bgDecorations) {
    drawBgDecoration(ctx, d);
  }
}
